
import React from "react";
import { useParams, Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { mockArticles } from "@/data/mockArticles";
import { ChevronLeft } from "lucide-react";

const ArticleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const article = mockArticles.find(a => a.id === parseInt(id || "0"));
  
  if (!article) {
    return (
      <MainLayout>
        <div className="container py-12 px-4">
          <div className="flex flex-col items-center justify-center py-12">
            <h1 className="text-2xl font-bold mb-4">文章不存在</h1>
            <p className="text-muted-foreground mb-6">
              很抱歉，找不到您要查看的文章
            </p>
            <Link to="/articles">
              <Button>返回文章列表</Button>
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container py-8">
        <div className="mb-6">
          <Link to="/articles" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
            <ChevronLeft className="h-4 w-4 mr-1" />
            返回文章列表
          </Link>
        </div>

        <article className="prose prose-slate lg:prose-lg mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <div>作者: {article.author}</div>
              <div>發布日期: {new Date(article.publishDate).toLocaleDateString()}</div>
              <div className="inline-block px-2 py-1 bg-primary-purple/10 text-primary-purple rounded-full">
                {article.category}
              </div>
            </div>
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Convert Markdown-like content to HTML (simplified version) */}
          <div 
            className="article-content"
            dangerouslySetInnerHTML={{ 
              __html: article.content
                .replace(/## (.*?)$/gm, '<h2>$1</h2>')
                .replace(/### (.*?)$/gm, '<h3>$1</h3>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .split('\n\n').join('</p><p>')
                .split('\n').join('<br />')
            }} 
          />
        </article>

        <div className="mt-12 border-t pt-8">
          <h3 className="text-xl font-bold mb-6">您可能也有興趣</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockArticles
              .filter(a => a.id !== article.id)
              .slice(0, 3)
              .map(relatedArticle => (
                <Link to={`/articles/${relatedArticle.id}`} key={relatedArticle.id}>
                  <div className="overflow-hidden rounded-lg border bg-card transition-colors hover:bg-accent/50 h-full">
                    <div className="aspect-video w-full overflow-hidden bg-muted">
                      <img
                        src={relatedArticle.coverImage}
                        alt={relatedArticle.title}
                        className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold mb-2 line-clamp-2">{relatedArticle.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {relatedArticle.excerpt}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ArticleDetail;
